/**
 * Utilitaires pour l'interpolation des templates d'emails
 */

import { Case } from "./useRealtimeCases";
import { formatParisDate, formatParisTime } from "./parisDateTime";

/**
 * Formater une date complète (ex: "15/03/2025 à 19h00")
 */
function formatFullDateTime(dateStr: string | null): string {
  if (!dateStr) return "à définir";
  try {
    const date = formatParisDate(dateStr, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const time = formatParisTime(dateStr, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${date} à ${time}`;
  } catch {
    return dateStr;
  }
}

/**
 * Formater un tableau en liste lisible (ex: ["Karting", "Bowling"] → "Karting, Bowling")
 */
function formatArray(arr: string[] | null | undefined): string {
  if (!arr || arr.length === 0) return "non spécifié";
  return arr.join(", ");
}

/**
 * Formater un montant (ex: 2500 → "2500€")
 */
function formatBudget(budget: number | null | undefined): string {
  if (!budget) return "à définir";
  return `${budget}€`;
}

/**
 * Interpoler un template avec les données d'un dossier
 * 
 * Exemple:
 * Template: "Bonjour {clientFirstName}, votre événement au {centerName}..."
 * Résultat: "Bonjour Marie, votre événement au Bercy..."
 * 
 * @param template - Le corps du template avec variables entre accolades
 * @param caseData - Les données du dossier
 * @returns Le template interpolé
 */
export function interpolateTemplate(template: string, caseData: Case | null): string {
  if (!caseData) return template;

  let result = template;

  // Remplacements des variables
  const replacements: Record<string, string> = {
    clientFirstName: caseData.client_first_name || "Client",
    clientLastName: caseData.client_last_name || "",
    clientEmail: caseData.client_email || "",
    clientPhone: caseData.client_phone || "",
    companyName: caseData.company_name || "",
    centerName: caseData.center || "votre centre",
    eventDate: formatFullDateTime(caseData.event_date),
    participants: String(caseData.participants || caseData.pax || 0),
    activities: formatArray(caseData.activities),
    occasion: caseData.occasion || "votre événement",
    budget: formatBudget(caseData.budget),
    address: caseData.address || "",
    city: caseData.city || "",
    postalCode: caseData.postal_code || "",
  };

  // Remplacer chaque variable dans le template
  for (const [key, value] of Object.entries(replacements)) {
    const regex = new RegExp(`\\{${key}\\}`, "g");
    result = result.replace(regex, value);
  }

  return result;
}

/**
 * Extraire les variables utilisées dans un template
 * Utile pour afficher un aperçu des variables disponibles
 * 
 * @param template - Le corps du template
 * @returns Liste des clés de variables trouvées (ex: ["clientFirstName", "centerName"])
 */
export function extractVariablesFromTemplate(template: string): string[] {
  const regex = /\{(\w+)\}/g;
  const matches = [...template.matchAll(regex)];
  const variables = matches.map((match) => match[1]);
  
  // Retirer les doublons
  return Array.from(new Set(variables));
}

/**
 * Valider qu'un template ne contient que des variables valides
 * 
 * @param template - Le corps du template
 * @param validVariables - Liste des clés de variables valides
 * @returns { valid: boolean, invalidVariables: string[] }
 */
export function validateTemplate(
  template: string,
  validVariables: string[]
): { valid: boolean; invalidVariables: string[] } {
  const usedVariables = extractVariablesFromTemplate(template);
  const invalidVariables = usedVariables.filter(
    (v) => !validVariables.includes(v)
  );

  return {
    valid: invalidVariables.length === 0,
    invalidVariables,
  };
}

/**
 * Générer un aperçu du template avec des données fictives
 * Utile pour la prévisualisation dans l'UI de création
 * 
 * @param template - Le corps du template
 * @returns Le template interpolé avec des données d'exemple
 */
export function previewTemplate(template: string): string {
  const fakeData: Case = {
    id: "fake-id",
    client_first_name: "Marie",
    client_last_name: "Dupont",
    client_email: "marie.dupont@example.com",
    client_phone: "06 12 34 56 78",
    company_name: "Acme Corp",
    center: "Bercy",
    event_date: new Date("2025-03-15T19:00:00").toISOString(),
    participants: 25,
    activities: ["Karting", "Bowling"],
    occasion: "Team Building",
    budget: 2500,
    address: "123 rue de la Paix",
    city: "Paris",
    postal_code: "75001",
    title: "Événement Acme Corp",
    status: "Non traité",
    created_at: new Date().toISOString(),
  } as any;

  return interpolateTemplate(template, fakeData);
}
